import { z } from "zod";

/** Paid services whose calls Polaris records (ADR 0062). */
export const API_PROVIDERS = ["openai", "apify"] as const;
export type ApiProvider = (typeof API_PROVIDERS)[number];

/** What a paid call was for. `dev_replay` is the `replay` script of the AI feedback review. */
export const API_USAGE_FEATURES = [
  "proposal",
  "followup",
  "transcription",
  "social_lookup",
  "web_search",
  "dev_replay",
  /** Embeddings of the messages we sent, for the examples of the proposals (ADR 0073). */
  "ai_examples",
  /** Embeddings of the "Quando serve" wiki pages (ADR 0075). */
  "ai_wiki",
  /** "Modifica con AI" of the workspace instructions or knowledge (ADR 0073). */
  "ai_text_edit",
  /** "Crea riepilogo" of a recorded call (ADR 0067). */
  "call_summary",
  /** The spam filter on a new contact's first message (ADR 0093). */
  "spam_check",
] as const;
export type ApiUsageFeature = (typeof API_USAGE_FEATURES)[number];

/** How the work that made the call started. */
export const API_USAGE_TRIGGERS = ["automatic", "manual", "scheduled", "script"] as const;
export type ApiUsageTrigger = (typeof API_USAGE_TRIGGERS)[number];

/**
 * `unusable`: billed but Polaris could not use the answer (incomplete, invalid or
 * thrown away); `failed`: an Apify run that failed after starting, billed for what
 * it used; `timeout`: Polaris gave up waiting, the provider may still bill it.
 */
export const API_USAGE_OUTCOMES = ["ok", "unusable", "failed", "timeout"] as const;
export type ApiUsageOutcome = (typeof API_USAGE_OUTCOMES)[number];

export const API_COST_CURRENCY = "USD";

/**
 * OpenAI price list, Standard tier, USD per million tokens or per minute of audio,
 * from https://developers.openai.com/api/docs/pricing. Changing a price means a new
 * `API_PRICE_VERSION`: every call keeps the cost and version it was priced with.
 */
export const API_PRICE_VERSION = "openai-2026-10-22";

/** OpenAI's `web_search` tool: 10 $ per thousand calls, the pages read billed as input tokens. */
export const OPENAI_WEB_SEARCH_CALL_PRICE = 0.01;

type TokenPrice = { input: number; cachedInput: number; output: number };
type OpenAiModelPrice = {
  tokens?: TokenPrice;
  /** Prices above this many input tokens in one request (long context). */
  longContext?: { aboveInputTokens: number; tokens: TokenPrice };
  perMinute?: number;
};

const LONG_CONTEXT_TOKENS = 272_000;

export const OPENAI_PRICES: Readonly<Record<string, OpenAiModelPrice>> = {
  "gpt-6.1-sol": {
    tokens: { input: 2, cachedInput: 0.1, output: 10 },
    longContext: {
      aboveInputTokens: LONG_CONTEXT_TOKENS,
      tokens: { input: 4, cachedInput: 0.2, output: 15 },
    },
  },
  "gpt-6-sol": {
    tokens: { input: 2, cachedInput: 0.2, output: 10 },
    longContext: {
      aboveInputTokens: LONG_CONTEXT_TOKENS,
      tokens: { input: 4, cachedInput: 0.4, output: 15 },
    },
  },
  "gpt-5.6-sol": {
    tokens: { input: 4, cachedInput: 0.4, output: 20 },
    longContext: {
      aboveInputTokens: LONG_CONTEXT_TOKENS,
      tokens: { input: 8, cachedInput: 0.8, output: 30 },
    },
  },
  "gpt-6-luna": {
    tokens: { input: 0.1, cachedInput: 0.01, output: 0.5 },
    longContext: {
      aboveInputTokens: LONG_CONTEXT_TOKENS,
      tokens: { input: 0.2, cachedInput: 0.02, output: 0.75 },
    },
  },
  "gpt-5-mini": { tokens: { input: 0.25, cachedInput: 0.025, output: 2 } },
  // Embeddings bill only the input (ADR 0073).
  "text-embedding-3-small": { tokens: { input: 0.02, cachedInput: 0.02, output: 0 } },
  "gpt-transcribe": { perMinute: 0.0045 },
  "gpt-4o-transcribe": { tokens: { input: 2.5, cachedInput: 2.5, output: 10 }, perMinute: 0.006 },
  "gpt-4o-mini-transcribe": {
    tokens: { input: 1.25, cachedInput: 1.25, output: 5 },
    perMinute: 0.003,
  },
  "whisper-1": { perMinute: 0.006 },
};

/**
 * The price list entry of a model as OpenAI names it in a response: the name
 * itself or a dated snapshot of it (`gpt-5-mini-2025-08-07`). Anything else has
 * no price rather than the price of a model with a similar name.
 */
export function openAiPriceModel(model: string): string | null {
  const name = model.trim().toLowerCase();
  if (OPENAI_PRICES[name]) return name;
  const snapshot = /^(.+)-\d{4}-\d{2}-\d{2}$/.exec(name);
  return snapshot?.[1] && OPENAI_PRICES[snapshot[1]] ? snapshot[1] : null;
}

export type ApiTokenUsage = {
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  /** Part of the output tokens, billed as output. */
  reasoningTokens: number | null;
};

const tokenCount = z.number().int().nonnegative();

const responseUsageSchema = z
  .object({
    input_tokens: tokenCount,
    input_tokens_details: z
      .object({ cached_tokens: tokenCount.optional() })
      .passthrough()
      .nullish(),
    output_tokens: tokenCount,
    output_tokens_details: z
      .object({ reasoning_tokens: tokenCount.optional() })
      .passthrough()
      .nullish(),
  })
  .passthrough();

/** Token usage of a Responses API reply, or null when it has none. */
export function readOpenAiResponseUsage(usage: unknown): ApiTokenUsage | null {
  const parsed = responseUsageSchema.safeParse(usage);
  if (!parsed.success) return null;
  return {
    inputTokens: parsed.data.input_tokens,
    cachedInputTokens: parsed.data.input_tokens_details?.cached_tokens ?? 0,
    outputTokens: parsed.data.output_tokens,
    reasoningTokens: parsed.data.output_tokens_details?.reasoning_tokens ?? null,
  };
}

export type TranscriptionUsage =
  | { kind: "duration"; seconds: number }
  | { kind: "tokens"; tokens: ApiTokenUsage; audioTokens: number | null };

const transcriptionUsageSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("duration"), seconds: z.number().nonnegative() }).passthrough(),
  z
    .object({
      type: z.literal("tokens"),
      input_tokens: tokenCount,
      input_token_details: z
        .object({ audio_tokens: tokenCount.optional() })
        .passthrough()
        .nullish(),
      output_tokens: tokenCount,
    })
    .passthrough(),
]);

/** Usage of a transcription: seconds of audio or tokens, depending on the model. */
export function readOpenAiTranscriptionUsage(usage: unknown): TranscriptionUsage | null {
  const parsed = transcriptionUsageSchema.safeParse(usage);
  if (!parsed.success) return null;
  if (parsed.data.type === "duration") return { kind: "duration", seconds: parsed.data.seconds };
  return {
    kind: "tokens",
    tokens: {
      inputTokens: parsed.data.input_tokens,
      cachedInputTokens: 0,
      outputTokens: parsed.data.output_tokens,
      reasoningTokens: null,
    },
    audioTokens: parsed.data.input_token_details?.audio_tokens ?? null,
  };
}

const roundCost = (value: number) => Math.round(value * 1_000_000) / 1_000_000;

/** Cost in USD of token usage, or null when the model has no token price. */
export function openAiTokenCost(model: string, usage: ApiTokenUsage): number | null {
  const key = openAiPriceModel(model);
  const price = key ? OPENAI_PRICES[key] : undefined;
  if (!price?.tokens) return null;
  const tokens =
    price.longContext && usage.inputTokens > price.longContext.aboveInputTokens
      ? price.longContext.tokens
      : price.tokens;
  const cached = Math.min(usage.cachedInputTokens, usage.inputTokens);
  return roundCost(
    ((usage.inputTokens - cached) * tokens.input +
      cached * tokens.cachedInput +
      usage.outputTokens * tokens.output) /
      1_000_000,
  );
}

/** Cost in USD of a transcription, or null when the model has no matching price. */
export function openAiTranscriptionCost(model: string, usage: TranscriptionUsage): number | null {
  if (usage.kind === "tokens") return openAiTokenCost(model, usage.tokens);
  const key = openAiPriceModel(model);
  const perMinute = key ? OPENAI_PRICES[key]?.perMinute : undefined;
  return perMinute === undefined ? null : roundCost((usage.seconds / 60) * perMinute);
}

/** Apify's cost of a run in USD, from the run object (`usageTotalUsd`). */
export function readApifyRunCost(run: unknown): number | null {
  const parsed = z.object({ usageTotalUsd: z.number().nonnegative() }).passthrough().safeParse(run);
  return parsed.success ? roundCost(parsed.data.usageTotalUsd) : null;
}

/** A paid call as the core reports it, before the worker adds workspace and lead. */
export type ApiUsageReport = {
  provider: ApiProvider;
  model: string | null;
  externalId: string | null;
  outcome: ApiUsageOutcome;
  tokens: ApiTokenUsage | null;
  details: Record<string, unknown>;
  cost: number | null;
  costSource: "price_list" | "provider";
  /** False while the provider may still change the cost (Apify, a few seconds after the run). */
  settled: boolean;
};

/** Receives every paid call. Recording must never make the call itself fail. */
export type ApiUsageRecorder = (report: ApiUsageReport) => Promise<void> | void;

/**
 * State of the OpenAI control, platform-wide (one project for every workspace),
 * kept in Redis by the API costs job. No amounts: only when and how it went.
 */
export const API_COSTS_STATE_KEY = "polaris:api-costs:openai";

export const apiCostsStateSchema = z
  .object({
    lastAttemptAt: z.string().datetime({ offset: true }).nullable().default(null),
    lastSyncedAt: z.string().datetime({ offset: true }).nullable().default(null),
    lastErrorAt: z.string().datetime({ offset: true }).nullable().default(null),
    lastError: z.string().nullable().default(null),
  })
  .strict();
export type ApiCostsState = z.infer<typeof apiCostsStateSchema>;

export function readApiCostsState(raw: string | null): ApiCostsState {
  try {
    const parsed = apiCostsStateSchema.safeParse(raw ? JSON.parse(raw) : {});
    if (parsed.success) return parsed.data;
  } catch {
    // An unreadable state counts as never synced.
  }
  return apiCostsStateSchema.parse({});
}

/** The OpenAI control: the workspace's share of OpenAI's own daily cost. */
export type ApiCostControlDto = {
  /** False without an Admin key: only price-list estimates are shown. */
  enabled: boolean;
  lastSyncedAt: string | null;
  lastError: string | null;
  /** Days of the month OpenAI has reported so far, with both figures. */
  days: number;
  estimatedCost: number;
  providerCost: number;
};

export type ApiCostsDto = {
  month: string;
  /** Months with calls, newest first, always including the current one. */
  months: string[];
  currency: typeof API_COST_CURRENCY;
  totals: {
    cost: number;
    calls: number;
    /** Calls without a price (model missing from the price list, or timeout). */
    unpricedCalls: number;
    /** Apify runs whose final cost is not read yet. */
    pendingCalls: number;
    /** Billed calls whose answer was not used. */
    unusableCost: number;
    unusableCalls: number;
  };
  features: Array<{ feature: ApiUsageFeature; cost: number; calls: number }>;
  models: Array<{
    provider: ApiProvider;
    model: string;
    cost: number;
    calls: number;
    inputTokens: number;
    cachedInputTokens: number;
    outputTokens: number;
    seconds: number;
  }>;
  days: Array<{ date: string; cost: number; calls: number }>;
  /** Leads that cost the most in the month, with their WhatsApp cost beside. */
  leads: Array<{
    leadId: string;
    displayName: string;
    cost: number;
    calls: number;
    whatsappCost: number;
    whatsappCurrency: string | null;
  }>;
  control: ApiCostControlDto;
};

/** What a lead's paid calls cost, for owners and admins. */
export type LeadApiCostDto = {
  cost: number;
  calls: number;
  unpricedCalls: number;
  pendingCalls: number;
  currency: typeof API_COST_CURRENCY;
  features: Array<{ feature: ApiUsageFeature; cost: number; calls: number }>;
};

/** One paid call in a lead's list. */
export type ApiUsageItemDto = {
  id: string;
  occurredAt: string;
  provider: ApiProvider;
  feature: ApiUsageFeature;
  trigger: ApiUsageTrigger | null;
  model: string | null;
  outcome: ApiUsageOutcome;
  inputTokens: number | null;
  cachedInputTokens: number | null;
  outputTokens: number | null;
  seconds: number | null;
  cost: number | null;
  currency: string;
  settled: boolean;
};

export type ApiUsagePageDto = { items: ApiUsageItemDto[]; nextCursor: string | null };
